import { useEffect, useState } from "react";
import { toast } from "sonner";
import { analysisAPI, icpAPI } from "../../lib/api";

const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";
const ACCENT = "#2563EB";
const GREEN = "#22C55E";
const AMBER = "#F59E0B";
const RED = "#EF4444";
const SHARED = "#A855F7";

const scoreColor = s => (s >= 70 ? GREEN : s >= 40 ? AMBER : RED);

const STATUS_STYLE = {
  match: { color: GREEN, glyph: "✓" },
  miss: { color: RED, glyph: "✕" },
  unknown: { color: TEXT_DIM, glyph: "?" },
  unspecified: { color: TEXT_DIM, glyph: "–" },
};

const DimensionChip = ({ dim }) => {
  const st = STATUS_STYLE[dim.status] || STATUS_STYLE.unknown;
  return (
    <span
      data-testid={`preset-dim-${dim.key}-${dim.status}`}
      title={dim.detail}
      style={{
        fontSize: 11, padding: "3px 9px", borderRadius: 14,
        display: "inline-flex", alignItems: "center", gap: 5,
        background: dim.status === "match" ? `${GREEN}14` : "transparent",
        border: `1px solid ${dim.status === "match" ? `${GREEN}44` : BORDER}`,
        color: st.color, whiteSpace: "nowrap",
      }}>
      <span style={{ fontSize: 9 }}>{st.glyph}</span>
      {dim.label}
    </span>
  );
};

const PresetRow = ({ row, isBest, onSwitch, onDeepScore, deepBusy, switchBusy }) => {
  const [open, setOpen] = useState(isBest);
  const deep = row.deep;
  const shown = deep ? deep.score : row.score;

  return (
    <div
      data-testid={`preset-fit-row-${row.preset_id}`}
      style={{
        background: isBest ? `${GREEN}08` : SURFACE_2,
        border: `1px solid ${isBest ? `${GREEN}44` : BORDER}`,
        borderRadius: 9, padding: "12px 14px", marginBottom: 8,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ minWidth: 46, textAlign: "center" }}>
          <div data-testid={`preset-fit-score-${row.preset_id}`} style={{
            fontSize: 20, fontWeight: 700, color: row.scored ? scoreColor(shown) : TEXT_DIM,
            fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em", lineHeight: 1.1,
          }}>
            {row.scored ? shown : "—"}
          </div>
          <div style={{ fontSize: 8, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
            {deep ? "DEEP" : "RULES"}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: TEXT_PRIMARY }}>{row.name}</span>
            {isBest && (
              <span data-testid="preset-fit-best-badge" style={{
                fontSize: 8.5, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em",
                padding: "2px 6px", borderRadius: 4, background: `${GREEN}18`,
                border: `1px solid ${GREEN}55`, color: GREEN,
              }}>BEST FIT</span>
            )}
            {row.is_active && (
              <span style={{
                fontSize: 8.5, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em",
                padding: "2px 6px", borderRadius: 4, background: `${ACCENT}18`,
                border: `1px solid ${ACCENT}55`, color: ACCENT,
              }}>ACTIVE</span>
            )}
            <span style={{
              fontSize: 8.5, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em",
              padding: "2px 5px", borderRadius: 4,
              border: `1px solid ${row.scope === "workspace" ? `${SHARED}55` : BORDER}`,
              color: row.scope === "workspace" ? SHARED : TEXT_DIM,
            }}>{row.scope === "workspace" ? "SHARED" : "MINE"}</span>
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 3, fontFamily: "'DM Mono', monospace" }}>
            {row.scored
              ? `${row.matched}/${row.judged} dimensions matched · ${row.coverage}% of criteria judgeable`
              : "Not enough evidence in this brief to judge this preset"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button data-testid={`preset-fit-expand-${row.preset_id}`} onClick={() => setOpen(o => !o)}
            style={{
              background: "none", border: `1px solid ${BORDER}`, color: TEXT_MUTED,
              borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}>
            {open ? "Hide" : "Why"}
          </button>
          {!row.is_active && (
            <button data-testid={`preset-fit-switch-${row.preset_id}`} onClick={() => onSwitch(row)} disabled={switchBusy}
              style={{
                background: isBest ? `${GREEN}18` : "none",
                border: `1px solid ${isBest ? `${GREEN}55` : BORDER}`,
                color: isBest ? GREEN : TEXT_MUTED, borderRadius: 6, padding: "6px 10px",
                fontSize: 11, cursor: switchBusy ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
                whiteSpace: "nowrap",
              }}>
              Switch to this
            </button>
          )}
        </div>
      </div>

      {open && (
        <div data-testid={`preset-fit-detail-${row.preset_id}`} style={{ marginTop: 11, paddingTop: 11, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {row.dimensions.map(d => <DimensionChip key={d.key} dim={d} />)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            {row.dimensions.filter(d => d.status === "match" || d.status === "miss").map(d => (
              <div key={d.key} style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.5 }}>
                <span style={{ color: d.status === "match" ? GREEN : RED, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>
                  {d.label.toUpperCase()}
                </span>{" "}
                {d.detail}
              </div>
            ))}
          </div>

          {deep ? (
            <div data-testid={`preset-fit-deep-${row.preset_id}`} style={{
              background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px",
            }}>
              <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", marginBottom: 6 }}>
                DEEP SCORE · {String(deep.verdict || "").toUpperCase()} · {deep.score}
              </div>
              <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.6, marginBottom: deep.strengths?.length || deep.gaps?.length ? 8 : 0 }}>
                {deep.rationale}
              </p>
              {(deep.strengths || []).map(s => (
                <div key={s} style={{ fontSize: 11.5, color: GREEN, lineHeight: 1.6 }}>+ {s}</div>
              ))}
              {(deep.gaps || []).map(g => (
                <div key={g} style={{ fontSize: 11.5, color: AMBER, lineHeight: 1.6 }}>− {g}</div>
              ))}
            </div>
          ) : (
            <button data-testid={`preset-fit-deep-btn-${row.preset_id}`} onClick={() => onDeepScore(row)} disabled={deepBusy}
              style={{
                background: "none", border: `1px dashed ${BORDER}`, color: deepBusy ? ACCENT : TEXT_MUTED,
                borderRadius: 6, padding: "7px 12px", fontSize: 11.5, cursor: deepBusy ? "wait" : "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}>
              {deepBusy ? "Scoring with the analyst…" : "Deep score with the analyst (uses credits)"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Scores this company against every ICP preset the user can see — personal
 * and workspace-shared — ranked best-first. Rules-based and free; a deep
 * LLM score can be requested per preset.
 */
export const PresetFitComparison = ({ workspaceId, analysisId, onPresetSwitched }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deepBusy, setDeepBusy] = useState(null);
  const [switchBusy, setSwitchBusy] = useState(false);

  const load = async () => {
    try {
      const res = await analysisAPI.presetScores(workspaceId, analysisId);
      setData(res.data);
    } catch (e) {
      setData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (workspaceId && analysisId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, analysisId]);

  const handleSwitch = async (row) => {
    setSwitchBusy(true);
    try {
      const res = await icpAPI.activatePreset(row.preset_id);
      const stale = res.data?.newly_marked_stale || 0;
      toast.success(`Switched to “${row.name}”`, {
        description: stale > 0 ? `${stale} brief${stale === 1 ? "" : "s"} marked for re-scoring.` : undefined,
      });
      await load();
      if (onPresetSwitched) onPresetSwitched();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch preset");
    }
    setSwitchBusy(false);
  };

  const handleDeepScore = async (row) => {
    setDeepBusy(row.preset_id);
    try {
      await analysisAPI.deepScorePreset(workspaceId, analysisId, row.preset_id);
      await load();
      toast.success(`Deep score ready for “${row.name}”`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deep score failed");
    }
    setDeepBusy(null);
  };

  if (loading || !data || !(data.presets || []).length) return null;

  const rows = data.presets;
  const best = data.best_preset_id;
  const activeRow = rows.find(r => r.is_active);
  const bestRow = rows.find(r => r.preset_id === best);
  const betterElsewhere = bestRow && activeRow && bestRow.preset_id !== activeRow.preset_id
    && bestRow.score > activeRow.score;

  return (
    <div data-testid="preset-fit-comparison" style={{ gridColumn: "1 / -1" }}>
      <div style={{
        background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10,
        overflow: "hidden", marginBottom: 12,
      }}>
        <div style={{
          padding: "11px 16px", borderBottom: `1px solid ${BORDER}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <span style={{
            fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace",
            letterSpacing: "0.1em", textTransform: "uppercase",
          }}>Preset Fit Comparison</span>
          <span style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>
            {rows.length} PRESET{rows.length === 1 ? "" : "S"} · FREE
          </span>
        </div>
        <div style={{ padding: "14px 16px" }}>
          {betterElsewhere && (
            <div data-testid="preset-fit-recommendation" style={{
              background: `${GREEN}0D`, border: `1px solid ${GREEN}33`, borderRadius: 8,
              padding: "10px 12px", marginBottom: 12, fontSize: 12.5, color: TEXT_MUTED, lineHeight: 1.6,
            }}>
              <strong style={{ color: GREEN, fontWeight: 600 }}>{data.company_name}</strong> fits{" "}
              <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>{bestRow.name}</strong> ({bestRow.score})
              better than your active preset{" "}
              <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>{activeRow.name}</strong> ({activeRow.score}).
            </div>
          )}
          {rows.map(row => (
            <PresetRow
              key={row.preset_id}
              row={row}
              isBest={row.preset_id === best}
              onSwitch={handleSwitch}
              onDeepScore={handleDeepScore}
              deepBusy={deepBusy === row.preset_id}
              switchBusy={switchBusy}
            />
          ))}
          <p style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.55, marginTop: 4 }}>
            Scores compare this brief's findings against each preset's criteria. Dimensions with no
            evidence are excluded rather than counted as misses — the coverage figure shows how much
            of each preset could be judged.
          </p>
        </div>
      </div>
    </div>
  );
};
